/**
 * Number formatting for physics results: significant figures, scientific notation and
 * SI prefixes ("4.7 kΩ", "25.53 mA").
 *
 * Electronics and wave answers run from picofarads to terahertz, so a plain `toFixed`
 * either shows nothing or a wall of digits. These helpers round to significant figures
 * and move to scientific notation or an SI prefix when the number calls for it, writing
 * the decimal mark the reader's language uses.
 */

export type NumberFormatter = (
  value: number,
  options?: Intl.NumberFormatOptions
) => string;

const SUPERSCRIPT_DIGITS = "⁰¹²³⁴⁵⁶⁷⁸⁹";
const MIN_SIG = 1;
const MAX_SIG = 15;
const PLAIN_LOW = 1e-3;
const PLAIN_HIGH = 1e6;

/** An integer exponent in Unicode superscripts: −8 → "⁻⁸". */
export function superscriptInt(value: number): string {
  let out = value < 0 ? "⁻" : "";
  for (const digit of String(Math.abs(value))) {
    out += SUPERSCRIPT_DIGITS[Number(digit)];
  }
  return out;
}

function clampSig(sig: number): number {
  return Math.min(Math.max(Math.round(sig), MIN_SIG), MAX_SIG);
}

/** `2.58 × 10²³`, with the mantissa in the reader's decimal mark. */
export function scientific(
  fmt: NumberFormatter,
  value: number,
  sig: number
): string {
  const digits = clampSig(sig);
  const [mantissa, exponent] = value.toExponential(digits - 1).split("e");
  const shown = fmt(Number(mantissa), {
    maximumFractionDigits: digits - 1,
  });
  return `${shown} × 10${superscriptInt(Number(exponent))}`;
}

/**
 * Round to at most `sig` significant figures (trailing zeros dropped) and write it: plain for everyday magnitudes
 * (0.001 up to 999,999), scientific notation beyond. Zero is "0".
 */
export function formatSig(
  fmt: NumberFormatter,
  value: number,
  sig: number
): string {
  if (!Number.isFinite(value)) {
    return "—";
  }
  if (value === 0) {
    return fmt(0);
  }
  const digits = clampSig(sig);
  const magnitude = Math.abs(value);
  if (magnitude < PLAIN_LOW || magnitude >= PLAIN_HIGH) {
    return scientific(fmt, value, digits);
  }
  return fmt(value, { maximumSignificantDigits: digits, useGrouping: false });
}

/** A full-precision echo of an intermediate value without float noise. */
export function formatPlain(fmt: NumberFormatter, value: number): string {
  if (!Number.isFinite(value)) {
    return "—";
  }
  const magnitude = Math.abs(value);
  if (magnitude !== 0 && (magnitude < PLAIN_LOW || magnitude >= 1e9)) {
    return scientific(fmt, value, 5);
  }
  return fmt(value, { maximumSignificantDigits: 8, useGrouping: false });
}

/** SI prefixes with the exponent of ten each stands for. */
export const SI_PREFIXES: readonly { symbol: string; exp: number }[] = [
  { symbol: "a", exp: -18 },
  { symbol: "f", exp: -15 },
  { symbol: "p", exp: -12 },
  { symbol: "n", exp: -9 },
  { symbol: "µ", exp: -6 },
  { symbol: "m", exp: -3 },
  { symbol: "", exp: 0 },
  { symbol: "k", exp: 3 },
  { symbol: "M", exp: 6 },
  { symbol: "G", exp: 9 },
  { symbol: "T", exp: 12 },
  { symbol: "P", exp: 15 },
  { symbol: "E", exp: 18 },
];

const FIRST_PREFIX = SI_PREFIXES[0].exp;
const LAST_PREFIX = SI_PREFIXES.at(-1)?.exp ?? 18;

/** Scale `value` by 10^exp without the rounding error `value * 10 ** exp` adds. */
export function shiftExp(value: number, exp: number): number {
  if (value === 0 || !Number.isFinite(value)) {
    return value;
  }
  const [mantissa, power] = value.toExponential().split("e");
  return Number(`${mantissa}e${Number(power) + exp}`);
}

/**
 * A value with the SI prefix that keeps its number between 1 and 1000: 0.02553 A →
 * "25.53 mA". Outside p…T it falls back to scientific notation on the bare unit.
 */
export function withSiPrefix(
  fmt: NumberFormatter,
  value: number,
  unit: string,
  sig: number
): string {
  if (!Number.isFinite(value)) {
    return "—";
  }
  if (value === 0) {
    return `${fmt(0)} ${unit}`;
  }
  const digits = clampSig(sig);
  const rounded = Number(value.toPrecision(digits));
  const exponent = Math.floor(Math.log10(Math.abs(rounded)));
  const step = Math.floor(exponent / 3) * 3;
  if (step < FIRST_PREFIX || step > LAST_PREFIX) {
    return `${scientific(fmt, value, digits)} ${unit}`;
  }
  const prefix = SI_PREFIXES.find((p) => p.exp === step)?.symbol ?? "";
  const scaled = shiftExp(rounded, -step);
  const shown = fmt(scaled, {
    maximumSignificantDigits: digits,
    useGrouping: false,
  });
  return `${shown} ${prefix}${unit}`;
}

/**
 * The SI prefix that suits an axis whose largest value is `max`: 0.0255 → milli.
 * Outside a…E (or for a non-positive max) the base unit is used.
 */
export function axisPrefix(max: number): { symbol: string; exp: number } {
  if (!(max > 0 && Number.isFinite(max))) {
    return { symbol: "", exp: 0 };
  }
  const step = Math.floor(Math.floor(Math.log10(max)) / 3) * 3;
  const found = SI_PREFIXES.find((p) => p.exp === step);
  return found ?? { symbol: "", exp: 0 };
}
